<div *transloco="let t">
  @if (show(); as currentShow) {
    <multisite-show-banner [show]="currentShow" [linkTo]="[definition.showsPath, currentShow.slug]"></multisite-show-banner>
  }

  <div class="container">
    @let current = episode();

    @if (current) {
      <div class="row">
        <div class="col-lg-8">
          <p class="text-muted mb-1 mt-2">
            @if (current.season !== null) {
              <span>{{ t('shows.season_number', { number: current.season }) }}</span>
              <span class="mx-1">·</span>
            }
            @if (current.number !== null) {
              <span>{{ t('shows.episode_number', { number: current.number }) }}</span>
              <span class="mx-1">·</span>
            }
            <span>{{ current.publishedAt | date: 'longDate' }}</span>
          </p>
          <h1 class="font-weight-bold text-break">{{ current.title }}</h1>

          @if (translation.translating()) {
            <div class="d-flex align-items-center my-2" role="status">
              <multisite-loader-globe-animation-detail [width]="40" [height]="40"></multisite-loader-globe-animation-detail>
              <span class="ml-2">{{ t('news.translating') }}</span>
            </div>
          }

          @if (current.cuts.length > 1) {
            <div class="btn-group my-2" role="group" [attr.aria-label]="t('shows.recordings')">
              @for (c of current.cuts; track c.id) {
                <button type="button" class="btn btn-sm" [class.btn-primary]="c.id === cutId()" [class.btn-outline-primary]="c.id !== cutId()" (click)="chooseCut(c)">
                  {{ c.medium === 'video' ? t('common.video') : t('shows.audio') }}
                  @if (cutLabel(c, current.cuts); as language) {
                    <span class="ml-1">· {{ language }}</span>
                  }
                </button>
              }
            </div>
          }

          @if (player(); as link) {
            <div class="my-3">
              @for (key of [playerKey()]; track key) {
                <multisite-show-player [link]="link" [medium]="cut()?.medium ?? 'video'" [startAt]="startAt()" [autoplay]="autoplay()" [title]="current.title"></multisite-show-player>
              }
            </div>
          }

          @if (alsoOn().length) {
            <p class="small mb-3">
              <span class="text-muted mr-1">{{ t('shows.other_platforms') }}</span>
              @for (link of alsoOn(); track link.url) {
                <a class="mr-2" [href]="link.url" target="_blank" rel="noopener">{{ channelNames[link.channel] }}</a>
              }
            </p>
          }

          @if (current.summary) {
            <div class="lead text-break" [innerHTML]="current.summary | insightMarkdown"></div>
          }

          @if (current.chapters.length) {
            <h2 class="h5 font-weight-bold mt-4">{{ t('shows.chapters') }}</h2>
            <ol class="list-unstyled">
              @for (chapter of current.chapters; track chapter.id) {
                @let time = chapterTime(chapter);
                <li class="mb-2" [class.font-weight-bold]="openChapterPosition() === chapter.position">
                  @if (chapterCut(chapter)) {
                    <button type="button" class="btn btn-link p-0 text-left" (click)="chooseChapter(chapter)">
                      @if (time !== null) {
                        <span class="text-monospace mr-2">{{ timecode(time) }}</span>
                      }
                      {{ chapter.title }}
                    </button>
                  } @else {
                    @if (time !== null) {
                      <span class="text-monospace mr-2">{{ timecode(time) }}</span>
                    }
                    {{ chapter.title }}
                  }
                  @if (chapter.description) {
                    <div class="small text-muted">{{ chapter.description }}</div>
                  }
                </li>
              }
            </ol>
          }

          @if (current.notes) {
            <div class="my-4 text-break" [innerHTML]="current.notes | insightMarkdown"></div>
          }

          @if (current.quotations.length) {
            <h2 class="h5 font-weight-bold mt-4">{{ t('shows.quotations') }}</h2>
            @for (quotation of current.quotations; track quotation.id) {
              <blockquote class="blockquote border-left pl-3 my-3">
                <p class="mb-1">{{ quotation.text }}</p>
                @if (quotation.originator) {
                  <footer class="blockquote-footer">
                    {{ quotation.originator }}
                    @if (quotation.originatorDescription) {
                      <span>, {{ quotation.originatorDescription }}</span>
                    }
                  </footer>
                }
              </blockquote>
            }
          }

          @if (current.links.length) {
            <h2 class="h5 font-weight-bold mt-4">{{ t('common.useful_links') }}</h2>
            <ul class="list-unstyled">
              @for (link of current.links; track link.id) {
                <li class="mb-2">
                  <a [href]="link.url" target="_blank" rel="noopener">{{ link.label || link.url }}</a>
                  @if (link.description) {
                    <div class="small text-muted">{{ link.description }}</div>
                  }
                </li>
              }
            </ul>
          }

          @if (current.clips.length) {
            <h2 class="h5 font-weight-bold mt-4">{{ t('shows.clips') }}</h2>
            <ul class="list-unstyled">
              @for (clip of current.clips; track clip.id) {
                <li class="mb-1">
                  <a [routerLink]="clipPath(definition, clip)">{{ clip.title }}</a>
                  <small class="text-muted ml-1">{{ t('shows.clip_kind.' + clip.kind) }}</small>
                </li>
              }
            </ul>
          }

          @if (current.related.length) {
            <h2 class="h5 font-weight-bold mt-4">{{ t('shows.related_reading') }}</h2>
            <ul>
              @for (reading of current.related; track reading.type + reading.id) {
                <li>{{ reading.title }}</li>
              }
            </ul>
          }

          <div class="mt-5">
            <comments-post-display-pagination-all-in-one
              [user]="user() ?? undefined"
              [hasPermissionToReadComments]="true"
              [hasPermissionToPostComments]="mayComment()"
              [disabled]="!mayComment()"
              [paginatedComments]="paginatedComments()"
              [loadingComments]="loadingComments()"
              [clearCommentForm]="clearCommentForm()"
              [titleText]="conversationName()"
              [titleClasses]="'h5'"
              [titleTranslationKey]="'social.leave_a_comment'"
              [descriptionTranslationKey]="'ideas.comments_in_your_language'"
              [descriptionTranslationScope]="'ideas'"
              (postComment)="postComment($event)"
              (getComments)="getComments($event)">
            </comments-post-display-pagination-all-in-one>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="sticky-top pt-3">
            <div class="mb-3">
              <multisite-shows-like-button [item]="ref()"></multisite-shows-like-button>
            </div>

            <div class="mb-3">
              <multisite-shows-subscribe-button [show]="showSlug()"></multisite-shows-subscribe-button>
            </div>

            <div class="mb-4">
              <multisite-shows-credits [crew]="credits()"></multisite-shows-credits>
            </div>

            @if (current.tools.length) {
              <p class="h5">{{ t('tools.tools') }}</p>
              <ul class="list-unstyled mb-4">
                @for (tool of current.tools; track tool.id) {
                  <li class="mb-2">
                    <a [routerLink]="[definition.toolsPath, tool.slug]">{{ tool.name }}</a>
                    @if (recommenders(tool); as names) {
                      @if (names.length) {
                        <div class="small text-muted">{{ t('tools.recommenders', { names: names.join(', ') }) }}</div>
                      }
                    }
                  </li>
                }
              </ul>
            }

            @if (moreEpisodes().length) {
              <p class="h5">{{ t('shows.more_episodes') }}</p>
              <div class="list-group list-group-flush">
                @for (other of moreEpisodes(); track other.id) {
                  <a class="list-group-item list-group-item-action px-0" [routerLink]="[definition.showsPath, other.show, other.slug]">
                    <span class="d-block">{{ other.title }}</span>
                    <small class="text-muted">{{ other.publishedAt | date: 'mediumDate' }}</small>
                  </a>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    @if (!current && loading()) {
      <div class="my-5"><multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail></div>
    }

    @if (notFound()) {
      <div class="text-center my-5">
        <p>{{ t('common.not_found') }}</p>
        <a [routerLink]="[definition.showsPath]">{{ t('shows.all_shows') }}</a>
      </div>
    }

    @if (failed()) {
      <p class="text-danger my-5 text-center">{{ t('error.could_not_load') }}</p>
    }
  </div>
</div>
